/**
 * FilterBottomSheet - iOS Style Filter Bottom Sheet
 * Complete filter interface for ItemList page
 */

import { useState } from 'react';
import { useQuery } from 'react-query';
import { ChevronDown, ChevronUp, MapPin, Navigation } from 'lucide-react';
import IOSheet from './IOSheet';
import { ItemType } from '../config/itemConfig';
import { ADMIN_LECCE_COORDS, UNIFIED_RADIUS_VALUES } from '../config/filters';
import api from '../utils/api';

interface FilterState {
  brand: string;
  model: string;
  min_price: string;
  max_price: string;
  year: string;
  min_length: string;
  max_length: string;
  engine_type: string;
  fuel_type: string;
  condition: string;
  min_hours: string;
  max_hours: string;
  horsepower: string;
  boat_type_code: string;
  accessory_type_code: string;
}

interface FilterBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  filters: FilterState;
  setFilters: React.Dispatch<React.SetStateAction<FilterState>>;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  location: { lat: number; lng: number } | null;
  setLocation: React.Dispatch<React.SetStateAction<{ lat: number; lng: number } | null>>;
  radius: number;
  setRadius: React.Dispatch<React.SetStateAction<number>>;
  useLocation: boolean;
  setUseLocation: React.Dispatch<React.SetStateAction<boolean>>;
  useFixedLocation: boolean;
  setUseFixedLocation: React.Dispatch<React.SetStateAction<boolean>>;
   availableOnly: boolean;
   setAvailableOnly: React.Dispatch<React.SetStateAction<boolean>>;
   statusFilter: string;
   setStatusFilter: React.Dispatch<React.SetStateAction<string>>;
   itemType: ItemType;
   onReset: () => void;
}

// Predefined filter options
const FILTER_OPTIONS: { id: keyof FilterState; label: string; type: 'text' | 'range' | 'select'; options?: { value: string; label: string }[] }[] = [
  { id: 'brand', label: 'Marca', type: 'text' },
  { id: 'model', label: 'Modello', type: 'text' },
  { id: 'min_price', label: 'Prezzo min', type: 'text' },
  { id: 'max_price', label: 'Prezzo max', type: 'text' },
  { id: 'year', label: 'Anno', type: 'text' },
  { id: 'min_length', label: 'Lunghezza min (m)', type: 'text' },
  { id: 'max_length', label: 'Lunghezza max (m)', type: 'text' },
];

const MOTOR_FILTER_OPTIONS = [
  { id: 'engine_type' as keyof FilterState, label: 'Tipo Motore', options: [
    { value: 'outboard', label: 'Fuoribordo' },
    { value: 'inboard', label: 'Entrobordo' },
    { value: ' sterndrive', label: 'Drive' },
  ]},
  { id: 'fuel_type' as keyof FilterState, label: 'Carburante', options: [
    { value: 'diesel', label: 'Diesel' },
    { value: 'benzina', label: 'Benzina' },
    { value: 'elettrico', label: 'Elettrico' },
  ]},
  { id: 'condition' as keyof FilterState, label: 'Condizione', options: [
    { value: 'new', label: 'Nuovo' },
    { value: 'used', label: 'Usato' },
    { value: 'km0', label: 'Km 0' },
  ]},
];

const BOAT_FILTER_OPTIONS = [
  { id: 'condition' as keyof FilterState, label: 'Condizione', options: [
    { value: 'new', label: 'Nuovo' },
    { value: 'used', label: 'Usato' },
    { value: 'km0', label: 'Km 0' },
  ]},
  { id: 'boat_type_code' as keyof FilterState, label: 'Tipologia', type: 'multi-select' as const, options: [
    { value: 'GOM', label: 'Gommone' },
    { value: 'BM', label: 'Barca Motore' },
    { value: 'FLY', label: 'Flybridge' },
    { value: 'OPE', label: 'Open' },
    { value: 'VEL', label: 'Vela' },
    { value: 'TND', label: 'Tender' },
  ]},
];

const ACCESSORY_FILTER_OPTIONS = [
  { id: 'condition' as keyof FilterState, label: 'Condizione', options: [
    { value: 'new', label: 'Nuovo' },
    { value: 'used', label: 'Usato' },
    { value: 'km0', label: 'Km 0' },
  ]},
  { id: 'accessory_type_code' as keyof FilterState, label: 'Tipologia', type: 'multi-select' as const, options: [
    { value: 'ELE', label: 'Elettronica' },
    { value: 'ANC', label: 'Ancoraggio' },
    { value: 'SIC', label: 'Sicurezza' },
    { value: 'COM', label: 'Comfort' },
    { value: 'ILL', label: 'Illuminazione' },
    { value: 'MOT', label: 'Ricambi Motore' },
    { value: 'CAR', label: 'Carrello' },
    { value: 'TEL', label: 'Teloni' },
    { value: 'MAN', label: 'Manutenzione' },
    { value: 'SPO', label: 'Sport Acquatici' },
    { value: 'PES', label: 'Pesca' },
    { value: 'ALI', label: 'Allestimento' },
  ]},
];

export default function FilterBottomSheet({
  isOpen,
  onClose,
  filters,
  setFilters,
  location: _location,
  setLocation,
  radius,
  setRadius,
  useLocation,
  setUseLocation,
  useFixedLocation,
  setUseFixedLocation,
   availableOnly,
   setAvailableOnly,
   statusFilter,
   setStatusFilter,
   itemType,
   onReset,
}: FilterBottomSheetProps) {
  // Fetch engine types from DB with fallback
  const { data: categoriesData } = useQuery(
    'categories-filter',
    () => api.get('/categories').then((res: any) => res.data?.data || res.data || []),
    { staleTime: 10 * 60 * 1000 }
  );
  
  // Extract engine types from categories
  const categoriesArray = Array.isArray(categoriesData) ? categoriesData : [];
  const engineTypes = categoriesArray.filter((c: any) => c?.type === 'engine_type').map((c: any) => c.name);
  const defaultEngineTypes = ['Entrofuoribordo', 'Fuoribordo', 'Entrobordo', 'Elettrico'];
  const engineTypeOptions = engineTypes.length > 0 ? engineTypes : defaultEngineTypes;
  
  const [expandedSections, setExpandedSections] = useState<string[]>(['filters', 'location']);
  const [tempFilters, setTempFilters] = useState<FilterState>(filters);
  
  const toggleSection = (section: string) => {
    setExpandedSections(prev =>
      prev.includes(section) ? prev.filter(s => s !== section) : [...prev, section]
    );
  };

  const handleApply = () => {
    setFilters(tempFilters);
    onClose();
  };

  const handleReset = () => {
    setTempFilters({
      brand: '', model: '', min_price: '', max_price: '',
      year: '', min_length: '', max_length: '',
      engine_type: '', fuel_type: '', condition: '',
      min_hours: '', max_hours: '', horsepower: '', boat_type_code: '', accessory_type_code: '',
    });
    onReset();
  };

  const isBoat = itemType === 'boats';
  const isAccessory = itemType === 'accessories';
  const extraOptions = isBoat ? BOAT_FILTER_OPTIONS : isAccessory ? ACCESSORY_FILTER_OPTIONS : MOTOR_FILTER_OPTIONS;
  
  // Dynamic engine type options for boats (from DB with fallback)
  const dynamicEngineTypeOptions = engineTypeOptions.map((type: string) => ({
    value: type,
    label: type,
  }));

  return (
    <IOSheet isOpen={isOpen} onClose={onClose} title="Filtri">
      <div className="flex flex-col h-full">
        {/* Filter Sections */}
        <div className="flex-1 overflow-y-auto">
          {/* Basic Filters Section */}
          <div className="border-b border-[var(--ios-separator)]">
            <button
              onClick={() => toggleSection('filters')}
              className="flex items-center justify-between w-full px-4 py-4 active:bg-[var(--ios-bg-system)]"
            >
              <span className="text-[17px] font-semibold text-[var(--ios-black)]">Filtri</span>
              {expandedSections.includes('filters') 
                ? <ChevronUp className="w-5 h-5 text-[var(--ios-gray)]" />
                : <ChevronDown className="w-5 h-5 text-[var(--ios-gray)]" />
              }
            </button>
            
            {expandedSections.includes('filters') && (
              <div className="px-4 pb-4 space-y-3">
                {FILTER_OPTIONS.map(filter => (
                  <div key={filter.id}>
                    <label className="text-[13px] text-[var(--ios-gray)] block mb-1">{filter.label}</label>
                    <input
                      type={filter.type}
                      value={tempFilters[filter.id] || ''}
                      onChange={(e) => setTempFilters(prev => ({ ...prev, [filter.id]: e.target.value }))}
                      placeholder={filter.label}
                      className="w-full px-3 py-2.5 bg-[var(--ios-bg-system)] rounded-lg text-[17px] text-[var(--ios-black)] placeholder-[var(--ios-gray)]"
                    />
                  </div>
                ))}
                
                {extraOptions.map((filter: any) => (
                  <div key={String(filter.id)}>
                    <label className="text-[13px] text-[var(--ios-gray)] block mb-1">{filter.label}</label>
                    <select
                      value={(tempFilters as any)[filter.id] || ''}
                      onChange={(e) => setTempFilters(prev => ({ ...prev, [filter.id]: e.target.value }))}
                      className="w-full px-3 py-2.5 bg-[var(--ios-bg-system)] rounded-lg text-[17px] text-[var(--ios-black)]"
                    >
                      <option value="">Tutti</option>
                      {(filter.id === 'engine_type' && isBoat) ? (
                        dynamicEngineTypeOptions.map((opt: any) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))
                      ) : (
                        filter.options?.map((opt: any) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))
                      )}
                    </select>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Location Section */}
          <div className="border-b border-[var(--ios-separator)]">
            <button
              onClick={() => toggleSection('location')}
              className="flex items-center justify-between w-full px-4 py-4 active:bg-[var(--ios-bg-system)]"
            >
              <span className="text-[17px] font-semibold text-[var(--ios-black)]">Posizione</span>
              {expandedSections.includes('location')
                ? <ChevronUp className="w-5 h-5 text-[var(--ios-gray)]" />
                : <ChevronDown className="w-5 h-5 text-[var(--ios-gray)]" />
              }
            </button>
            
            {expandedSections.includes('location') && (
              <div className="px-4 pb-4 space-y-3">
                {/* Fixed Location Option */}
                <button
                  onClick={() => {
                    setUseFixedLocation(true);
                    setUseLocation(false);
                    setLocation(null);
                  }}
                  className={`flex items-center gap-3 w-full p-3 rounded-lg ${
                    useFixedLocation ? 'bg-[var(--ios-blue)]/10' : 'bg-[var(--ios-bg-system)]'
                  }`}
                >
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    useFixedLocation ? 'border-[var(--ios-blue)]' : 'border-[var(--ios-gray)]'
                  }`}>
                    {useFixedLocation && <div className="w-2.5 h-2.5 rounded-full bg-[var(--ios-blue)]" />}
                  </div>
                  <Navigation className="w-5 h-5 text-[var(--ios-gray)]" />
                  <span className="text-[15px] text-[var(--ios-black)]">{ADMIN_LECCE_COORDS.name} (default)</span>
                </button>

                {/* GPS Location Option */}
                <button
                  onClick={() => {
                    setUseLocation(true);
                    setUseFixedLocation(false);
                    if (navigator.geolocation) {
                      navigator.geolocation.getCurrentPosition(
                        (pos) => setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
                        () => alert('Impossibile ottenere la posizione')
                      );
                    }
                  }}
                  className={`flex items-center gap-3 w-full p-3 rounded-lg ${
                    useLocation ? 'bg-[var(--ios-blue)]/10' : 'bg-[var(--ios-bg-system)]'
                  }`}
                >
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    useLocation ? 'border-[var(--ios-blue)]' : 'border-[var(--ios-gray)]'
                  }`}>
                    {useLocation && <div className="w-2.5 h-2.5 rounded-full bg-[var(--ios-blue)]" />}
                  </div>
                  <MapPin className="w-5 h-5 text-[var(--ios-gray)]" />
                  <span className="text-[15px] text-[var(--ios-black)]">Usa la mia posizione</span>
                </button>

                {/* Radius Slider */}
                <div className="mt-4">
                  <div className="flex justify-between text-[13px] text-[var(--ios-gray)] mb-2">
                    <span>Raggio</span>
                    <span className="text-[var(--ios-blue)]">{radius} km</span>
                  </div>
                  <input
                    type="range"
                    min={String(UNIFIED_RADIUS_VALUES[0])}
                    max={String(UNIFIED_RADIUS_VALUES[UNIFIED_RADIUS_VALUES.length - 2])}
                    step={String(UNIFIED_RADIUS_VALUES[1])}
                    value={radius}
                    onChange={(e) => setRadius(parseInt(e.target.value))}
                    className="w-full h-2 bg-[var(--ios-bg-system)] rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-[11px] text-[var(--ios-gray)] mt-1">
                    <span>{UNIFIED_RADIUS_VALUES[0]}km</span>
                    <span>{UNIFIED_RADIUS_VALUES[UNIFIED_RADIUS_VALUES.length - 2]}km</span>
                   </div>
                 </div>

                  {/* Status Filter */}
                  <div className="mt-4">
                    <span className="text-[13px] text-[var(--ios-gray)] font-medium">Stato</span>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <button
                        onClick={() => setStatusFilter('')}
                        className={`flex-1 min-w-[70px] px-3 py-1.5 text-xs font-medium bt-badge ${
                          statusFilter === '' 
                            ? 'bg-blue-600 text-white' 
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        Tutti
                      </button>
                      <button
                        onClick={() => setStatusFilter('available')}
                        className={`flex-1 min-w-[70px] px-3 py-1.5 text-xs font-medium bt-badge flex items-center justify-center gap-1 ${
                          statusFilter === 'available' 
                            ? 'bg-green-600 text-white' 
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        <span className={`w-2 h-2 rounded-full ${statusFilter === 'available' ? 'bg-white' : 'bg-green-500'}`} />
                        Disp.
                      </button>
                      <button
                        onClick={() => setStatusFilter('reserved')}
                        className={`flex-1 min-w-[70px] px-3 py-1.5 text-xs font-medium bt-badge flex items-center justify-center gap-1 ${
                          statusFilter === 'reserved' 
                            ? 'bg-yellow-500 text-white' 
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        <span className={`w-2 h-2 rounded-full ${statusFilter === 'reserved' ? 'bg-white' : 'bg-yellow-500'}`} />
                        Pren.
                      </button>
                      <button
                        onClick={() => setStatusFilter('sold')}
                        className={`flex-1 min-w-[70px] px-3 py-1.5 text-xs font-medium bt-badge flex items-center justify-center gap-1 ${
                          statusFilter === 'sold' 
                            ? 'bg-red-600 text-white' 
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        <span className={`w-2 h-2 rounded-full ${statusFilter === 'sold' ? 'bg-white' : 'bg-red-500'}`} />
                        Vend.
                      </button>
                    </div>
                  </div>

                 {/* Available Only Toggle */}
                <button
                  onClick={() => setAvailableOnly(!availableOnly)}
                  className={`flex items-center justify-between w-full p-3 rounded-lg mt-2 ${
                    availableOnly ? 'bg-[var(--ios-blue)]/10' : 'bg-[var(--ios-bg-system)]'
                  }`}
                >
                  <span className="text-[15px] text-[var(--ios-black)]">Solo disponibili</span>
                  <div className={`w-11 h-6 rounded-full p-1 transition-colors ${
                    availableOnly ? 'bg-[var(--ios-green)]' : 'bg-[var(--ios-light-gray)]'
                  }`}>
                    <div className={`w-4 h-4 rounded-full bg-[var(--glass-card-bg,#fff)] transition-transform ${
                      availableOnly ? 'translate-x-5' : ''
                    }`} />
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="border-t border-[var(--ios-separator)] p-4 space-y-3 bg-[var(--glass-card-bg,#fff)]">
          <button
            onClick={handleApply}
            className="w-full py-3 bg-[var(--ios-blue)] text-white rounded-full text-[17px] font-semibold"
          >
            Applica Filtri
          </button>
          <button
            onClick={handleReset}
            className="w-full py-3 text-[var(--ios-red)] text-[15px] font-medium"
          >
            Reset Tutti i Filtri
          </button>
        </div>
      </div>
    </IOSheet>
  );
}
